import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Image,
  ActivityIndicator,
} from 'react-native';
import { useNavigation, useTheme } from '@react-navigation/native';
import { Icon } from '../../../components/atoms';
import { Theme } from '@theme';
import { getCommonStyles } from '@utils';
import ContactsList from './ContactsList';
import AddContactsModal from './AddContactsModal';
import { useGetContactById } from '@hooks';
import { defaultImage } from '@assets';

const ContactsListEdit = ({ isEdit = false, selectedContactIds, title, fitParent = false, setSelectedContactIds, onClose }: { isEdit?: boolean, selectedContactIds: string[], title?: string, fitParent?: boolean, setSelectedContactIds: React.Dispatch<React.SetStateAction<string[]>>, onClose?: () => void }) => {
  const theme = useTheme();
  const commonStyles = getCommonStyles(theme);
  const styles = getStyle(theme);
  const navigation = useNavigation()
  const [isAddContactModal, setIsAddContactModal] = useState(false);

  const SelectedContactItem = ({ contactId }: { contactId: string }) => {
    const { data: contact, isLoading } = useGetContactById(contactId);

    if (isLoading) {
      return (
        <View style={styles.selectedContactItem}>
          <ActivityIndicator size="small" color={theme.colors.primary} />
        </View>
      );
    }

    if (!contact) {
      return null;
    }

    return (
      <TouchableOpacity
        style={styles.selectedContactItem}
        onPress={() => {
          (navigation as any).navigate('Profile', {
            screen: 'DetailsGhostProfile',
            params: { profileId: contactId }
          })
        }}
      >
        <View style={styles.selectedAvatarWrapper}>
          <Image source={contact.avatarUrl? { uri: contact.avatarUrl } : defaultImage} style={styles.selectedContactAvatar} />
          {contact.isGhost && (
            <View style={styles.ghostBadge} pointerEvents="none">
              <Icon name="ghost" size={10} color={theme.colors.purple} />
            </View>
          )}
        </View>
        <Text style={styles.selectedContactName}>{contact.fullName}</Text>
        <TouchableOpacity style={styles.removeButton} onPress={() => {
          setSelectedContactIds(prev => prev.filter(id => id !== contactId));
        }}>
          <Icon name="cross" size={16} color={theme.colors.greyBlue} />
        </TouchableOpacity>
      </TouchableOpacity>
    );
  };

  const renderSelectedContact = (contactId: string) => (
    <SelectedContactItem key={contactId} contactId={contactId} />
  );

  return (
    <>
      <View style={[commonStyles.flex, fitParent && styles.container]}>
        {fitParent && <TouchableOpacity style={styles.closeButton} onPress={onClose}>
          <Icon name="cross" size={20} color={theme.colors.black} />
        </TouchableOpacity>}
        <ContactsList
          isEdit={isEdit}
          selectedContactIds={selectedContactIds}
          setSelectedContactIds={setSelectedContactIds}
          renderSelectedContact={renderSelectedContact}
          onAddContact={() => setIsAddContactModal(true)}
          title={title}
          fitParent={fitParent}
        />
      </View>
      <AddContactsModal
        isVisible={isAddContactModal}
        setIsVisible={setIsAddContactModal}
        fitParent={fitParent}
        selectedContactIds={selectedContactIds}
        setSelectedContactIds={setSelectedContactIds}
      />
    </>
  );
};

const getStyle = (theme: Theme) =>
  StyleSheet.create({
    container: {
      paddingVertical: theme.spacing.md,
      maxHeight: 400,
    },
    closeButton: {
      position: 'absolute',
      top: theme.spacing.xxxl,
      right: theme.spacing.md,
      zIndex: 1000,
    },
    selectedContactItem: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: theme.colors.white,
      borderRadius: 12,
      padding: 10,
    },
    selectedAvatarWrapper: {
      position: 'relative',
      marginRight: 12,
    },
    selectedContactAvatar: {
      width: 32,
      height: 32,
      borderRadius: 16,
    },
    ghostBadge: {
      position: 'absolute',
      right: 0,
      bottom: 0,
      width: 14,
      height: 14,
      borderRadius: 7,
      backgroundColor: theme.colors.white,
      borderWidth: 1,
      borderColor: theme.colors.purple,
      alignItems: 'center',
      justifyContent: 'center',
    },
    selectedContactName: {
      flex: 1,
      fontSize: 16,
      color: theme.colors.text,
    },
    removeButton: {
      padding: 4,
    },
  });

export default ContactsListEdit; 